import { Button } from "@progress/kendo-react-buttons";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { useEffect, useState, useRef } from "react";

import "./tabs.css"

/**
 * Generic Kendo Tabs Component
 * @param {tabs} Array<{id, title, content, icon?, disabled?}>
 * @param {selectedTabId} Optional controlled selected tab id
 * @param {onTabChange} Callback(tab, index) when tab changes
 * @param {closable} Boolean to allow closing tabs
 */
const GenericTabs = ({ tabs = [], selectedTabId = null, onTabChange = () => {}, closable = false , style={} , className = "" , initialSelIndex = 0 }) => {
  const [currentTabs, setCurrentTabs] = useState(tabs);
  const [selectedIndex, setSelectedIndex] = useState(initialSelIndex);
  const [leftArrowEnabled, setLeftArrowEnabled] = useState(false);
  const [rightArrowEnabled, setRightArrowEnabled] = useState(false);
  const navRef = useRef(null);

  // Sync tabs and selection
  useEffect(() => {
    setCurrentTabs(tabs);
    if (selectedTabId) {
      const idx = tabs.findIndex((t) => t.id === selectedTabId);
      if (idx !== -1) setSelectedIndex(idx);
    }
  }, [tabs, selectedTabId]);

  // Handle overflow arrows
  useEffect(() => {
    const nav = navRef.current?.querySelector('.k-tabstrip-items');
    if (!nav) return;

    const checkOverflow = () => {
      setLeftArrowEnabled(nav.scrollLeft > 0);
      setRightArrowEnabled(nav.scrollLeft + nav.clientWidth < nav.scrollWidth);
    };

    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(nav);
    nav.addEventListener('scroll', checkOverflow);
    window.addEventListener('resize', checkOverflow);

    return () => {
      resizeObserver.disconnect();
      nav.removeEventListener('scroll', checkOverflow);
      window.removeEventListener('resize', checkOverflow);
    };
  }, [currentTabs]);

  const handleTabSelect = (e) => {
    setSelectedIndex(e.selected);
    onTabChange(currentTabs[e.selected], e.selected);
  };

  const handleCloseTab = (tabId, idx) => {
    const newTabs = currentTabs.filter((t) => t.id !== tabId);
    setCurrentTabs(newTabs);

    let newIndex = selectedIndex;
    if (selectedIndex >= newTabs.length) newIndex = newTabs.length - 1;
    setSelectedIndex(newIndex);
    onTabChange(newTabs[newIndex], newIndex);
  };

  const scrollTabs = (direction) => {
    const nav = navRef.current?.querySelector('.k-tabstrip-items');
    if (nav) {
      const items = nav.querySelectorAll('li.k-item');
      const scrollAmount = items[0]?.offsetWidth || 100;
      nav.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <div ref={navRef}  style={{ height: '100%', minHeight: 0 }} >
      <TabStrip
        selected={selectedIndex}
        onSelect={handleTabSelect}
        scrollable={true}
        prevButton={() => (
          <Button
            icon="arrow-chevron-left"
            disabled={!leftArrowEnabled}
            onClick={leftArrowEnabled ? () => scrollTabs('left') : undefined}
          />
        )}
        nextButton={() => (
          <Button
            icon="arrow-chevron-right"
            disabled={!rightArrowEnabled}
            onClick={rightArrowEnabled ? () => scrollTabs('right') : undefined}
          />
        )}
        style={ style }
        className={className}
      >
        {currentTabs.map((tab, idx) => (
          <TabStripTab
            key={tab.id}
            disabled={tab.disabled}
            title={
              closable ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  {tab.icon && <span className={tab.icon}></span>}
                  {tab.title}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCloseTab(tab.id, idx);
                    }}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <>
                  {tab.icon && <span className={tab.icon}></span>}
                  {tab.title}
                </>
              )
            }
          >
            {tab.content}
          </TabStripTab>
        ))}
      </TabStrip>
    </div>
  );
};

export default GenericTabs;
